import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { clone } from 'ramda';
import GanttCell from './gant-cell';
import GanttCaseCell from './gantt-case-cell';
import { gray } from '../../theme';
import { BASE_URL, calculateTimeDiff, debugLogToConsole, padZero } from '../../utils';

const GanttRow = (props) => {
  const [rowData, setRowData] = useState([]);
  const [overlapRows, setOverlapRows] = useState(0);
  if (props.idx === 3) {
    window.rowData = rowData;
  }

  // _____ PROPS _____________________________________________________
  const { startHour = 0, endHour = 23, hourWidth = 98, idx, practice } = props;

  // _____ REFS ______________________________________________________
  const casesDataRef = useRef(true);

  // _____ EFFECTS ___________________________________________________
  const getCasesData = async () => {
    const casesData = [];
    for (let i = 0; i < rowData.length; i++) {
      if (rowData[i].cases.length > 0) {
        const caseIds = JSON.stringify(rowData[i].cases);
        const formData = new FormData();
        formData.append('caseIds', caseIds);

        const reqHeaders = new Headers();
        const token = localStorage.getItem('token');
        reqHeaders.append('Authorization', `Bearer ${token}`);

        const requestOptions = {
          method: 'POST',
          body: formData,
          redirect: 'follow',
          headers: reqHeaders
        };

        const response = await fetch(`${BASE_URL}/caseBlock/getCaseBlockCases`, requestOptions);
        const result = await response.json();

        if (result.success) {
          casesData.push(result.cases);
        }
      }
    }

    const updatedRowData = clone(rowData);
    updatedRowData.forEach((el, idx) => {
      el.cases = casesData[idx];
    });

    // Finding row position
    let blocksRanges = [];
    let isOverlap = false;
    let overlapCount = 0;

    // Step 1: Find each case blocks time range
    updatedRowData.forEach((row, idx) => {
      // blockEndDate: "2024-04-24T00:00:00.000Z"
      const { blockStartDate, blockEndDate } = row;
      const sDate = new Date(Date.parse(blockStartDate));
      const bsYear = sDate.getFullYear();
      const bsMonth = sDate.getMonth();
      const bsDate = sDate.getDate();

      const eDate = new Date(Date.parse(blockEndDate));
      const beYear = eDate.getFullYear();
      const beMonth = eDate.getMonth();
      const beDate = eDate.getDate();

      let currentBlockStartHour = 23;
      let currentBlockStartMin = 59;
      let currentBlockEndHour = 0;
      let currentBlockEndMin = 0;

      if (row.isLinked) {
        if (row.cases && row.cases.length > 0) {
          // Iteraring over cases in each case block
          row.cases.forEach((_case) => {
            currentBlockStartHour = Math.min(currentBlockStartHour, _case.scheduleStartHour);
            currentBlockStartMin = Math.min(currentBlockStartMin, _case.scheduleStartMinute);
            currentBlockEndHour = Math.max(currentBlockEndHour, _case.scheduleEndHour);
            currentBlockEndMin = Math.max(currentBlockEndMin, _case.scheduleEndMinute);
          });

          blocksRanges.push({
            blockId: row._id,
            startEpochTime: new Date(bsYear, bsMonth, bsDate, currentBlockStartHour, currentBlockStartMin).getTime(),
            startHour: currentBlockStartHour,
            startMinute: currentBlockStartMin,
            endEpochTime: new Date(beYear, beMonth, beDate, currentBlockEndHour, currentBlockEndMin).getTime(),
            endHour: currentBlockEndHour,
            endMinute: currentBlockEndMin
          });
        }
      } else {
        blocksRanges.push({
          blockId: row._id,
          startEpochTime: new Date(bsYear, bsMonth, bsDate, currentBlockStartHour, currentBlockStartMin).getTime(),
          startHour: row.blockStartHour,
          startMinute: row.blockStartMinute,
          endEpochTime: new Date(beYear, beMonth, beDate, currentBlockEndHour, currentBlockEndMin).getTime(),
          endHour: row.blockEndHour,
          endMinute: row.blockEndMinute
        });
      }
    });

    // Step 2: Sort block ranges in ascending order by startHour
    // blocksRanges = blocksRanges.sort((a, b) => a.startHour - b.startHour);
    blocksRanges = blocksRanges.sort((a, b) => a.startEpochTime - b.startEpochTime);

    // Step 3: Find overlaps
    const tmpBlockRanges = [];
    blocksRanges.forEach((br) => {
      debugLogToConsole(br);
      if (tmpBlockRanges.length === 0) {
        tmpBlockRanges.push({
          ...br,
          position: overlapCount
        });
      } else {
        // let tmpStartHour = 23;
        // let tmpStartMinute = 59;
        // let tmpEndHour = 0;
        // let tmpEndMinute = 0;
        let tmpStartEpochTime = 4859722800000;    // Jan 1, 2124 00:00
        let tmpEndEPochTime = 0;
        tmpBlockRanges.forEach(el => {
          // tmpStartHour = Math.min(tmpStartHour, el.startHour);
          // tmpStartMinute = Math.min(tmpStartMinute, el.startMinute);
          // tmpEndHour = Math.max(tmpEndHour, el.endHour);
          // tmpEndMinute = Math.max(tmpEndMinute, el.endMinute);
          tmpStartEpochTime = Math.min(tmpStartEpochTime, el.startEpochTime);
          tmpEndEPochTime = Math.max(tmpEndEPochTime, el.endEpochTime);
        });
        // const hourDiff = br.startHour - tmpEndHour;
        // const minDiff = br.startMinute - tmpEndMinute;
        // if (hourDiff > 0) {
        //   isOverlap = false;
        // } else if (hourDiff === 0) {
        //   isOverlap = minDiff < 0;
        // } else if (hourDiff < 0) {
        //   isOverlap = true;
        // }
        isOverlap = br.startEpochTime - tmpEndEPochTime < 60;
        if (isOverlap) {
          overlapCount += 1;
          tmpBlockRanges.push({
            ...br,
            position: overlapCount
          });
        } else {
          tmpBlockRanges.push({
            ...br,
            position: 0
          });
        }
      }
    });

    tmpBlockRanges.forEach((el) => {
      const cbIndex = rowData.findIndex(cb => cb._id === el.blockId);
      updatedRowData[cbIndex].position = el.position;
    });
    // Step 4: Set Row Data including cases data
    setOverlapRows(overlapCount);
    props.onRowHeightCalculated(props.idx, (overlapCount + 1) * props.rowHeight);
    setRowData(updatedRowData);
  };

  useEffect(() => {
    // Initial data load
    if (casesDataRef.current) {
      debugLogToConsole('Gant Row props.data effect - intial data load');
      setRowData(props.data);
    }

    // Subsequent data load
    if (!casesDataRef.current && rowData.length !== props.data.length) {
      debugLogToConsole('Gant Row props.data effect - subsequent data load');
      setRowData(props.data);
      casesDataRef.current = true;
    }
  }, [props.data]);

  // useEffect(() => {
  //   if (props.reloadOrphanedCases) {
  //     props.resetOrphanedCasesReloadFlag();
  //     setRowData(props.data);
  //   }
  // }, [props.reloadOrphanedCases]);

  useEffect(() => {
    if (props.type === 'caseblock' && casesDataRef.current && rowData.length > 0) {
      casesDataRef.current = false;
      getCasesData();
    }
  }, [rowData]);

  // _____ STYLES ____________________________________________________
  const styles = StyleSheet.create({
    ganttRow: {
      position: 'relative',
      borderBottom: `1px solid ${gray[700]}`,
      width: `${(endHour - startHour + 1) * hourWidth + 8}px`,
      background: 'black',
      paddingTop: '1px',
      paddingBottom: '1px'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      background: '#103A60',
      width: '220px',
      maxWidth: '220px',
      minHeight: '80px',
      fontSize: '14px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      // width: '138px',
      width: '100%',
      paddingLeft: '8px',
      background: '#103A60',
      ':not(.active):hover': {
        background: '#0066CC',
        color: 'white'
      }
    },
    gridEllipsisMenuItemActive: {
      background: gray[700],
      color: 'white'
    }
  });

  const handleOnUpdateBlockAnes = (anes, cbId) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('caseBlockId', cbId);
    formData.append('anes', JSON.stringify({
      addedPractices: [],
      avatarImageUrl: anes.avatarImageUrl,
      icon: anes.avatarImageUrl,
      caseRelation: anes.specialty,
      credentials: anes.credentials,
      fullName: `${anes.lastName}, ${anes.firstName}`,
      npi: anes.npi,
      text: `${anes.lastName}, ${anes.firstName}`,
      user: anes._id
    }));

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    fetch(`${BASE_URL}/caseBlock/syncAnesthesiologist`, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          casesDataRef.current = true;
          props.onReloadData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnUpdateBlockCrna = (crna, cbId) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('caseBlockId', cbId);
    formData.append('crna', JSON.stringify({
      addedPractices: [],
      avatarImageUrl: crna.avatarImageUrl,
      icon: crna.avatarImageUrl,
      caseRelation: crna.specialty,
      credentials: crna.credentials,
      fullName: `${crna.lastName}, ${crna.firstName}`,
      npi: crna.npi,
      text: `${crna.lastName}, ${crna.firstName}`,
      user: crna._id
    }));

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    fetch(`${BASE_URL}/caseBlock/syncCrna`, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          casesDataRef.current = true;
          props.onReloadData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickEditCaseBlock = async (cbData) => {
    const cbPatientIds = [];
    const cbPatientsData = {};
    if (cbData.cases && Array.isArray(cbData.cases)) {
      cbData.cases.forEach((cbCase) => {
        if (cbCase.patientId && cbCase.patientId.length > 0) {
          cbPatientIds.push(cbCase.patientId);
        }
      })
    }

    if (cbPatientIds.length > 0) {
      // Get patients data
      const formData = new FormData();
      formData.append('patientIds', JSON.stringify(cbPatientIds));
      // Request Headers incl Authorization
      const reqHeaders = new Headers();
      const token = localStorage.getItem('token');
      reqHeaders.append('Authorization', `Bearer ${token}`);
      const requestOptions = {
        method: 'POST',
        body: formData,
        redirect: 'follow',
        headers: reqHeaders
      };

      const response = await fetch(`${BASE_URL}/patients/getPatientsData`, requestOptions);
      const result = await response.json();

      if (result.success) {
        result.patientsData.forEach((el) => {
          cbPatientsData[el._id] = el;
        });
      }
    }
    props.onEditCaseBlock(cbData._id, cbData.cases, cbPatientsData);
  };

  // _____ CELLS _____________________________________________________
  const cells = [];
  rowData.forEach((el, idx) => {
    if (props.type !== 'assignment') {
      let blockStartHour = 0;
      let blockStartMinute = 0;
      let blockEndHour = 0;
      let blockEndMinute = 0;

      if (el.isLinked) {
        blockStartHour = el.linkedStartHour;
        blockStartMinute = el.linkedStartMinute;
        blockEndHour = el.linkedEndHour;
        blockEndMinute = el.linkedEndMinute;
      } else {
        blockStartHour = el.blockStartHour;
        blockStartMinute = el.blockStartMinute;
        blockEndHour = el.blockEndHour;
        blockEndMinute = el.blockEndMinute;
      }
      const blockDuration = calculateTimeDiff(`${blockStartHour}:${blockStartMinute}`, `${blockEndHour}:${blockEndMinute}`);
      const left = (blockStartHour + (blockStartMinute / 60)) * hourWidth + (24 / 98 * hourWidth);
      const width = blockDuration / 60 * hourWidth;
      let surgStr = 'None';
      let anesStr = 'None';
      let crnaStr = 'None';

      if (el.surgeon && Object.keys(el.surgeon).length > 0) {
        const surgeonName = el.surgeon.fullName.split(',');
        const surgeonFirstName = surgeonName[1].trim().charAt(0).toUpperCase();
        const surgeonLastName = surgeonName[0].trim();
        surgStr = surgeonFirstName.length > 0 && surgeonLastName.length > 0 ? `${surgeonFirstName}.${surgeonLastName}` : 'None';
      }

      if (el.anesthesiologist && Object.keys(el.anesthesiologist).length > 0) {
        const anesName = el.anesthesiologist.fullName.split(',');
        const anesFirstName = anesName[1].trim().charAt(0).toUpperCase();
        const anesLastName = anesName[0].trim();
        anesStr = anesFirstName.length > 0 && anesLastName.length > 0 ? `${anesFirstName}.${anesLastName}` : 'None';
      }

      if (el.crna && Object.keys(el.crna).length > 0) {
        const crnaName = el.crna.fullName.split(',');
        const crnaFirstName = crnaName[1].trim().charAt(0).toUpperCase();
        const crnaLastName = crnaName[0].trim();
        crnaStr = crnaFirstName.length > 0 && crnaLastName.length > 0 ? `${crnaFirstName}.${crnaLastName}` : 'None';
      }

      if (el.isOrphanedCase) {
        cells.push(
          <GanttCaseCell
            key={`${el._id}_cell`}
            id={el._id}
            left={left}
            width={width}
            height={props.rowHeight}
            position={el.position}
            surgStr={surgStr}
            anesStr={anesStr}
            crnaStr={crnaStr}
            practiceId={practice._id}
            cbCasesData={el}
            isOrphanedCase={el.isOrphanedCase}
            onEditCaseBlock={handleOnClickEditCaseBlock}
            onUpdateBlockAnes={handleOnUpdateBlockAnes}
            onUpdateBlockCrna={handleOnUpdateBlockCrna}
            onReloadData={() => {
              casesDataRef.current = true;
              props.onReloadData();
            }}
          />
        );
      } else {
        cells.push((
          <GanttCell
            key={`${el._id}_cell`}
            id={el._id}
            left={left}
            width={width}
            height={props.rowHeight}
            position={el.position}
            surgStr={surgStr}
            anesStr={anesStr}
            crnaStr={crnaStr}
            practiceId={practice._id}
            cbCasesData={el}
            isOrphanedCase={el.isOrphanedCase}
            onEditCaseBlock={handleOnClickEditCaseBlock}
            onUpdateBlockAnes={handleOnUpdateBlockAnes}
            onUpdateBlockCrna={handleOnUpdateBlockCrna}
            onReloadData={() => {
              casesDataRef.current = true;
              props.onReloadData();
            }}
          />
        ));
      }
    } else {
      const blockStartHour = el.startHour;
      const blockStartMinute = el.startMinute;
      const blockEndHour = el.stopHour;
      const blockEndMinute = el.stopMinute;
      const blockDuration = calculateTimeDiff(`${blockStartHour}:${blockStartMinute}`, `${blockEndHour}:${blockEndMinute}`);
      const left = (blockStartHour + (blockStartMinute / 60)) * hourWidth + (24 / 98 * hourWidth);
      const width = blockDuration / 60 * hourWidth;

      let dirStr = 'None';

      if (el.director && Object.keys(el.director).length > 0) {
        const directorName = el.director.fullName.split(',');
        const directorFirstName = directorName[1].trim().charAt(0).toUpperCase();
        const directorLastName = directorName[0].trim();
        dirStr = directorFirstName.length > 0 && directorLastName.length > 0 ? `${directorFirstName}.${directorLastName}` : 'None';
      }

      if (el.provider && Object.keys(el.provider).length > 0) {
        const directorName = el.provider.fullName.split(',');
        const directorFirstName = directorName[1].trim().charAt(0).toUpperCase();
        const directorLastName = directorName[0].trim();
        dirStr = directorFirstName.length > 0 && directorLastName.length > 0 ? `${directorFirstName}.${directorLastName}` : 'None';
      }

      cells.push((
        <GanttCell
          key={`${el._id}_cell`}
          type='assignment'
          id={el._id}
          left={left}
          width={width}
          height={props.rowHeight}
          position={0}
          dirStr={dirStr}
          practiceId={practice._id}
          dbCasesData={el}
          startHour={padZero(blockStartHour)}
          startMinute={padZero(blockStartMinute)}
          stopHour={padZero(blockEndHour)}
          stopMinute={padZero(blockEndMinute)}
          onReloadData={() => {
            casesDataRef.current = true;
            props.onReloadData();
          }}
          onEditCaseBlock={() => {
            console.log('Not implemented!');
          }}
          onUpdateBlockAnes={() => {
            console.log('Not implemented!');
          }}
          onUpdatedBlockCrna={() => {
            console.log('Not implemented!');
          }}
        />
      ));
    }
  });

  return (
    <>
      <div
        className={css(styles.ganttRow)}
        style={{
          height: `${(overlapRows + 1) * props.rowHeight}px`,
          // maxHeight: '108px',
          // maxHeight: `${(props.rowHeight - 2) * 2}px`,
          maxHeight: `${props.rowHeight * 2}px`,
          overflowY: 'auto'
        }}
      >
        {cells}
      </div>
    </>
  );
};

GanttRow.defaultProps = {
  rowHeight: '56'
};
export default GanttRow;
